import { create } from 'zustand';
import { createLogger } from '@/lib/logger';

const log = createLogger('ToastStore');
const MAX_TOASTS = 5;
const TOAST_DURATION_MS = 2800;

/** Active auto-dismiss timers — cleaned up on manual removeToast */
const _timers = new Map<string, ReturnType<typeof setTimeout>>();

export interface ToastItem {
  id: string;
  message: string;
  type: 'success' | 'info' | 'warning';
}

interface ToastState {
  toasts: ToastItem[];
  addToast: (message: string, type?: ToastItem['type']) => void;
  removeToast: (id: string) => void;
}

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  addToast: (message, type = 'success') => {
    const id = `t_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    log.debug('Toast added', { id, type, message });
    set((s) => {
      const next = [...s.toasts, { id, message, type }];
      // Cap to MAX_TOASTS, evicting oldest first (#263)
      if (next.length > MAX_TOASTS) {
        // #539: Clean up timers for evicted toasts
        const evicted = next.slice(0, next.length - MAX_TOASTS);
        for (const t of evicted) {
          const timer = _timers.get(t.id);
          if (timer) { clearTimeout(timer); _timers.delete(t.id); }
        }
      }
      return { toasts: next.slice(-MAX_TOASTS) };
    });
    // Auto-dismiss with tracked timer (#297)
    const timer = setTimeout(() => {
      _timers.delete(id);
      set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
    }, TOAST_DURATION_MS);
    _timers.set(id, timer);
  },
  removeToast: (id) => {
    // Clear auto-dismiss timer if still pending (#297)
    const timer = _timers.get(id);
    if (timer) { clearTimeout(timer); _timers.delete(id); }
    set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) }));
  },
}));
